import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Clock, ChefHat, CheckCircle, Play, RotateCcw, Volume2, VolumeX } from 'lucide-react';

export default function KDS() {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [lastOrderCount, setLastOrderCount] = useState(0);

    const fetchOrders = useCallback(async () => {
        try {
            const response = await api.get('/kds/orders?status=all');
            const newOrders = response.data;
            
            // Play sound if new orders arrived
            if (soundEnabled && newOrders.length > lastOrderCount && lastOrderCount > 0) {
                playNotificationSound();
            }
            
            setLastOrderCount(newOrders.length);
            setOrders(newOrders);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
        } finally {
            setLoading(false);
        }
    }, [soundEnabled, lastOrderCount]);

    useEffect(() => {
        fetchOrders();
        const interval = setInterval(fetchOrders, 10000); // Refresh every 10 seconds
        return () => clearInterval(interval);
    }, [fetchOrders]);

    const playNotificationSound = () => {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(() => {});
    };

    const startOrder = async (orderId) => {
        try {
            await api.post(`/kds/orders/${orderId}/start`);
            fetchOrders();
            toast.success('Order started');
        } catch (error) {
            toast.error('Failed to start order');
        }
    };

    const markReady = async (orderId) => {
        try {
            await api.post(`/kds/orders/${orderId}/ready`);
            fetchOrders();
            toast.success('Order marked ready');
        } catch (error) {
            toast.error('Failed to mark ready');
        }
    };

    const completeOrder = async (orderId) => {
        try {
            await api.post(`/kds/orders/${orderId}/complete`);
            fetchOrders();
            toast.success('Order completed');
        } catch (error) {
            toast.error('Failed to complete order');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'bg-emerald-500 text-white';
            case 'in_progress': return 'bg-amber-500 text-white';
            case 'ready': return 'bg-blue-500 text-white';
            case 'completed': return 'bg-gray-500 text-white';
            default: return 'bg-gray-500 text-white';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'pending': return 'NEW';
            case 'in_progress': return 'COOKING';
            case 'ready': return 'READY';
            case 'completed': return 'DONE';
            default: return status;
        }
    };

    // Filter orders by status
    const pendingOrders = orders.filter(o => o.status === 'pending');
    const inProgressOrders = orders.filter(o => o.status === 'in_progress');
    const readyOrders = orders.filter(o => o.status === 'ready');

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-950 flex items-center justify-center">
                <div className="animate-spin h-12 w-12 border-4 border-cyan-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-950 text-white p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <ChefHat className="h-8 w-8 text-cyan-500" />
                    <h1 className="text-2xl font-bold">Kitchen Display</h1>
                </div>
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSoundEnabled(!soundEnabled)}
                        className="text-white"
                    >
                        {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
                    </Button>
                    <Button variant="outline" onClick={fetchOrders} className="border-gray-700 text-white">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-emerald-500/20 border border-emerald-500/50 rounded-lg p-4">
                    <p className="text-emerald-400 text-sm font-medium">NEW</p>
                    <p className="text-3xl font-bold">{pendingOrders.length}</p>
                </div>
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4">
                    <p className="text-amber-400 text-sm font-medium">COOKING</p>
                    <p className="text-3xl font-bold">{inProgressOrders.length}</p>
                </div>
                <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-4">
                    <p className="text-blue-400 text-sm font-medium">READY</p>
                    <p className="text-3xl font-bold">{readyOrders.length}</p>
                </div>
                <div className="bg-muted/50 border border-border rounded-lg p-4">
                    <p className="text-muted-foreground text-sm font-medium">TOTAL ACTIVE</p>
                    <p className="text-3xl font-bold">{orders.filter(o => o.status !== 'completed').length}</p>
                </div>
            </div>

            {/* Orders Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {orders.filter(o => o.status !== 'completed').map((order) => (
                    <Card key={order.id} className={`${getStatusColor(order.status)} border-0`}>
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <Badge variant="secondary" className="bg-black/30 text-white border-0">
                                    {getStatusLabel(order.status)}
                                </Badge>
                                <span className="text-xs opacity-70">
                                    {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                <span className="font-mono text-lg font-bold">#{order.receipt_number}</span>
                                {order.source !== 'pos' && (
                                    <Badge variant="outline" className="text-xs border-white/30">
                                        {order.source}
                                    </Badge>
                                )}
                            </div>
                            <p className="text-sm opacity-90">{order.customer_name}</p>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="space-y-1">
                                {order.items.map((item, idx) => (
                                    <div key={idx} className="flex justify-between text-sm">
                                        <span>{item.quantity}x {item.product_name}</span>
                                    </div>
                                ))}
                            </div>
                            
                            {order.notes && (
                                <p className="text-xs bg-black/20 p-2 rounded italic">
                                    Note: {order.notes}
                                </p>
                            )}

                            <div className="pt-2 border-t border-white/20">
                                {order.status === 'pending' && (
                                    <Button 
                                        onClick={() => startOrder(order.id)}
                                        className="w-full bg-white/20 hover:bg-white/30 text-white border-0"
                                    >
                                        <Play className="h-4 w-4 mr-2" />
                                        Start Cooking
                                    </Button>
                                )}
                                {order.status === 'in_progress' && (
                                    <Button 
                                        onClick={() => markReady(order.id)}
                                        className="w-full bg-white/20 hover:bg-white/30 text-white border-0"
                                    >
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Mark Ready
                                    </Button>
                                )}
                                {order.status === 'ready' && (
                                    <Button 
                                        onClick={() => completeOrder(order.id)}
                                        className="w-full bg-white/20 hover:bg-white/30 text-white border-0"
                                    >
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Complete
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {orders.filter(o => o.status !== 'completed').length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                    <ChefHat className="h-16 w-16 mb-4 opacity-50" />
                    <p className="text-xl">No active orders</p>
                    <p className="text-sm">New orders will appear here automatically</p>
                </div>
            )}
        </div>
    );
}
